import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "components/ui/select";
import { useFileUpload } from "hooks/useFileUpload";
import { FileUp, RefreshCw, Trash2, Languages, Headphones, AlertTriangle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";

import { Link, useParams } from "react-router-dom";

const UploadPage = () => {
  const {projectId} = useParams();
  /* business-logic hook */
  const {
    fileData,
    fileCost,
    loading,
    loadingMessage,
    uploadError,
    selectedFile,
    youtubeUrl,
    label,
    selectedLanguage,
    availableLanguages,
    selectedVoice,
    voices,
    actionLoading,
    tags,
    newTagName,
    selectedTagId,
    audioType,
    /* setters */
    setYoutubeUrl,
    setLabel,
    setSelectedLanguage,
    setSelectedVoice,
    setSelectedFile,
    setSelectedTagId,
    setNewTagName,
    setStepByStep,
    /* drop-zone */
    getRootProps,
    getInputProps,
    /* actions */
    handleUpload,
    handleAction,
    handleDelete,
    /* quota */
    voiceoverQuotaWarning,
    stepByStep,
    multipleVoicesMode,
    setMultipleVoicesMode,
    voiceMapping,
    setVoiceMapping,
    voiceMappingDefault,
    setVoiceMappingDefault,
    isYoutubeImportWaiting,
    isYoutubeImportFailed,
    blockNewUpload,
  } = useFileUpload();

  const uploadDisabled =
    (!selectedFile && youtubeUrl.trim() === "") ||
    loading ||
    blockNewUpload;

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6">
      {!projectId && <div className="mb-8"><Link to="/" className="text-sm text-emerald-700">← All projects</Link><h1 className="text-3xl font-semibold tracking-tight mt-5 mb-2">Start with your content</h1><p className="text-muted-foreground">Give an idea a home. Every step will be saved together in your project.</p></div>}
      {!projectId && <Card className="mb-6 border-emerald-200 bg-emerald-50/60"><CardContent className="p-5"><p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Your first project</p><p className="mt-2 text-sm text-emerald-950">Add one source, then choose a translation or transcript. Voiceover comes after the text is ready, and you can leave at any time without losing your place.</p><div className="mt-3 grid gap-2 text-xs text-emerald-900 sm:grid-cols-3"><span>1. Upload or paste a link</span><span>2. Review the result</span><span>3. Translate or record</span></div></CardContent></Card>}
      {/* global loading banner */}
      {loadingMessage && !isYoutubeImportWaiting && (
        <div className="flex justify-center items-center py-4">
          <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
          <p className="text-sm text-muted-foreground">{loadingMessage}</p>
        </div>
      )}

      {/* global error banner (YouTube failed uses the dedicated card below) */}
      {uploadError && !isYoutubeImportFailed && (
        <div className="mb-6 p-4 border border-destructive/50 bg-destructive/10 text-destructive rounded-md flex items-center">
          <Trash2 className="h-5 w-5 mr-2" />
          <p className="text-sm font-medium">{uploadError}</p>
        </div>
      )}

      {/* YouTube import in progress – only wait UI, no other actions */}
      {fileData && isYoutubeImportWaiting && (
        <Card>
          <CardContent className="py-16 text-center space-y-6 px-6">
            <RefreshCw className="h-12 w-12 mx-auto animate-spin text-muted-foreground" />
            <div className="space-y-2">
              <CardTitle className="text-center text-xl">
                Downloading audio from YouTube
              </CardTitle>
              <p className="text-sm text-muted-foreground max-w-lg mx-auto leading-relaxed">
                This may take up to 10 minutes. You can return to your projects
                and open this step again while we work.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* YouTube import failed – remove before trying again */}
      {fileData && isYoutubeImportFailed && !loading && (
        <Card>
          <CardHeader>
            <CardTitle className="text-center">YouTube import failed</CardTitle>
            <CardDescription className="text-center">
              Remove this entry to upload a different file or link.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {uploadError && (
              <p className="text-sm text-destructive text-center">{uploadError}</p>
            )}
          </CardContent>
          <CardFooter className="flex justify-center">
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={actionLoading}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Remove and try again
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* UPLOAD CARD – shown until a file is uploaded */}
      {!fileData && !loading && (
        <Card>
          <CardHeader>
            <CardTitle className="text-center">
              Upload Your File or Paste YouTube Link
            </CardTitle>
            <CardDescription className="text-center">
              Supported formats: PDF, DOC(X), TXT, ODT, MP3, WAV
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* drop-zone */}
            <div
              {...getRootProps()}
              className="border-2 border-dashed border-border/60 rounded-md p-8 text-center hover:bg-accent/30 transition-colors cursor-pointer"
            >
              <input {...getInputProps()} />
              <FileUp className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              {selectedFile ? (
                <p>Selected file: {selectedFile.name}</p>
              ) : (
                <p className="text-muted-foreground">
                  Click or drag & drop your file here
                </p>
              )}
            </div>

            {/* OR divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-background px-4 text-sm text-muted-foreground">
                  OR
                </span>
              </div>
            </div>

            {/* YouTube input */}
            <div className="space-y-2">
              <label htmlFor="youtubeUrl" className="text-sm font-medium">
                YouTube URL:
              </label>
              <Input
                id="youtubeUrl"
                value={youtubeUrl}
                disabled={blockNewUpload}
                onChange={(e) => {
                  setYoutubeUrl(e.target.value);
                  if (e.target.value) {
                    /* clear previously chosen file */
                    setSelectedFile(null);
                  }
                }}
                placeholder="Paste a YouTube link here"
              />
            </div>
          </CardContent>

          <CardFooter>
            <Button
              onClick={handleUpload}
              disabled={uploadDisabled || loading}
              className="w-full"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Uploading…
                </>
              ) : (
                "Upload"
              )}
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* FILE DETAILS – shown after upload (not during YouTube wait / failed-only card) */}
      {fileData && !loading && !isYoutubeImportWaiting && !isYoutubeImportFailed && (
        <Card>
          <CardHeader>
            <CardTitle className="text-center">What would you like to create?</CardTitle>
            <CardDescription className="text-center">
              Choose what to do with the uploaded content
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* task label */}
            <div className="space-y-2">
              <label htmlFor="taskName" className="text-sm font-medium">
                Name this next step
              </label>
              <Input
                id="taskName"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="For example, Spanish translation or narrated version"
              />
            </div>

            {/* file info */}
            <div className="bg-accent/30 p-4 rounded-md">
              <h3 className="text-sm font-medium mb-1">File Information</h3>
              <p className="text-sm text-muted-foreground">
                {fileData.filename}
                {fileData.filetype.includes("youtube") && <> • YouTube Video</>}
              </p>
            </div>

            {voices.length === 0 && !audioType && <p className="text-sm text-muted-foreground">Want to create audio? <Link to="/voices" className="text-emerald-700 underline">Choose a voice in the voice library</Link> first, then return to this step.</p>}
            {audioType ? (
              //* transcript *//
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Transcript</h3>
                <div className="flex flex-wrap gap-3 items-center">
                  <Button
                    onClick={() => handleAction("transcript")}
                    disabled={actionLoading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {actionLoading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Transcribing…
                      </>
                    ) : (
                      <>
                        <Languages className="h-4 w-4 mr-2" />
                        Create transcript
                      </>
                    )}
                  </Button>

                  {fileCost && (
                    <span className="text-sm bg-green-100 text-green-800 py-1 px-2 rounded-md">
                      Cost: {fileCost.transcript}
                    </span>
                  )}
                  <p className="w-full text-xs text-muted-foreground">Usually ready in 1–3 minutes. The exact points cost is shown before you start.</p>
                </div>
              </div>
            ) : (
              <>
                {/* translation */}
                <div className="space-y-2">
                  <h3 className="text-sm font-medium">Translation</h3>
                  <div className="flex flex-wrap gap-3 items-center">
                    <Button
                      onClick={() => handleAction("translation")}
                      disabled={actionLoading}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {actionLoading ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Translating…
                        </>
                      ) : (
                        <>
                          <Languages className="h-4 w-4 mr-2" />
                          Translate
                        </>
                      )}
                    </Button>

                    <Select
                      value={selectedLanguage}
                      onValueChange={setSelectedLanguage}
                    >
                      <SelectTrigger className="w-44">
                        <SelectValue placeholder="Target language" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableLanguages.map((lang) => (
                          <SelectItem key={lang.code} value={lang.code}>
                            {lang.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {fileCost && (
                      <span className="text-sm bg-green-100 text-green-800 py-1 px-2 rounded-md">
                        Cost: {fileCost.translate}
                      </span>
                    )}
                    <p className="w-full text-xs text-muted-foreground">Usually ready in 1–5 minutes. You’ll see the points cost before processing begins.</p>
                  </div>
                </div>

                {/* voice-over */}
                <div className="space-y-2">
                  <h3 className="text-sm font-medium">Voice-over</h3>
                  <div className="flex flex-wrap gap-3 items-center">
                    <Button
                      onClick={() => handleAction("voiceover")}
                      disabled={actionLoading || !!voiceoverQuotaWarning}
                    >
                      {actionLoading ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Generating…
                        </>
                      ) : (
                        <>
                          <Headphones className="h-4 w-4 mr-2" />
                          Voice-over
                        </>
                      )}
                    </Button>

                    {fileData?.is_multiple_voices && (
                      <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={multipleVoicesMode}
                          onChange={(e) => setMultipleVoicesMode(e.target.checked)}
                          disabled={actionLoading}
                          className="w-4 h-4 rounded border-gray-300 accent-primary"
                        />
                        <span>Озвучить разными голосами</span>
                      </label>
                    )}

                    {!multipleVoicesMode && (
                      <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={stepByStep}
                          onChange={(e) => setStepByStep(e.target.checked)}
                          disabled={actionLoading}
                          className="w-4 h-4 rounded border-gray-300 accent-primary"
                        />
                        <span>Step-by-step</span>
                      </label>
                    )}
                    {multipleVoicesMode && (
                      <div className="flex items-center gap-2 text-sm text-gray-500 italic">
                        <span>Step-by-step mode enabled</span>
                      </div>
                    )}

                    {voiceoverQuotaWarning && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <span className="text-sm">{voiceoverQuotaWarning}</span>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    <p className="w-full text-xs text-muted-foreground">Usually ready in 2–10 minutes, depending on the length. The points cost is shown above.</p>

                    {!multipleVoicesMode && (
                      <Select value={selectedVoice} onValueChange={setSelectedVoice}>
                        <SelectTrigger className="w-44">
                          <SelectValue placeholder="Select voice" />
                        </SelectTrigger>
                        <SelectContent>
                          {voices.map((v) => (
                            <SelectItem key={v.id} value={v.id}>
                              {v.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {fileCost && (
                      <span className="text-sm bg-blue-100 text-blue-800 py-1 px-2 rounded-md">
                        Cost: {fileCost.voiceover}
                      </span>
                    )}
                  </div>

                  {multipleVoicesMode && fileData?.voice_tags && (
                    <div className="mt-4 p-4 bg-accent/30 border border-border/50 rounded-md space-y-3">
                      <h4 className="text-sm font-medium mb-3">Assign Voices to Tags</h4>
                      {fileData.voice_tags.map((tag: string) => (
                        <div key={tag} className="flex items-center justify-between text-sm">
                          <span className="font-semibold text-primary">[{tag}]</span>
                          <Select
                            value={voiceMapping[tag] || ""}
                            onValueChange={(val) => setVoiceMapping(prev => ({ ...prev, [tag]: val }))}
                          >
                            <SelectTrigger className="w-44 bg-background">
                              <SelectValue placeholder="Select voice" />
                            </SelectTrigger>
                            <SelectContent>
                              {voices.map((v) => (
                                <SelectItem key={v.id} value={v.id}>
                                  {v.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}

                      <div className="flex items-center justify-between text-sm border-t border-border/50 pt-3 mt-3">
                        <span className="font-semibold italic">Общий текст (без тега)</span>
                        <Select
                          value={voiceMappingDefault || selectedVoice}
                          onValueChange={setVoiceMappingDefault}
                        >
                          <SelectTrigger className="w-44 bg-background">
                            <SelectValue placeholder="Select default voice" />
                          </SelectTrigger>
                          <SelectContent>
                            {voices.map((v) => (
                              <SelectItem key={v.id} value={v.id}>
                                {v.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* tag / new-tag selector */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Tag</h3>
              <div className="flex items-center space-x-2">
                {/* input for new tag name */}
                <Input
                  placeholder="new tag"
                  value={newTagName}
                  maxLength={30}
                  onChange={e => {
                    const filtered = e.target.value.replace(/[^\w\s-]/g, '');
                    setNewTagName(filtered);
                    setSelectedTagId("");          // clear any selected existing tag
                  }}
                  className="w-44"
                />

                {tags.length > 0 && (
                  <>
                    <span className="text-sm text-muted-foreground">- or -</span>

                    {/* select for existing tags */}
                    <Select
                      value={selectedTagId}
                      onValueChange={value => {
                        setSelectedTagId(value);
                        setNewTagName("");          // clear any typed new tag
                      }}
                    >
                      <SelectTrigger className="w-44">
                        <SelectValue placeholder="Choose tag" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem key="-no tag-" value=" ">
                          - no tag -
                        </SelectItem>
                        {tags.map(tag => (
                          <SelectItem key={tag.id} value={tag.id}>
                            {tag.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>
            </div>
          </CardContent>

          <CardFooter className="flex justify-end">
            {projectId ? <Link className="text-sm text-emerald-700" to="/">Back to all projects →</Link> : <>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={actionLoading}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete & upload new file
            </Button></>}
          </CardFooter>
        </Card>
      )}
    </div>
  );
};

export default UploadPage;
